import { groupForFamily, type OntologyGroup } from '@/frontend/features/Fde/ontologyGraphGroups'
import type { ActionType, DataBinding, LinkType, OntologyClass, OntologyInterface } from '@/frontend/features/Fde/types'

export interface SchemaNodeRecord {
  id: string
  recordId: string
  kind: 'class' | 'family' | 'interface'
  familyId: string | null
  name: string
  description: string | null
  group: OntologyGroup
  source: 'baseline' | 'project'
  status: 'proposto' | 'confirmado'
  propertyCount: number
  elementCount: number
  linkCount: number
  actionCount: number
  backingCount: number
}

export interface SchemaEdgeRecord {
  id: string
  source: string
  target: string
  label: string
  cardinality: string | null
  kind: 'link' | 'implements' | 'extends' | 'summary'
}

export interface SchemaProjection { nodes: SchemaNodeRecord[]; edges: SchemaEdgeRecord[] }

export function buildSchemaProjection(classes: OntologyClass[], interfaces: OntologyInterface[], linkTypes: LinkType[], actionTypes: ActionType[], bindings: DataBinding[], showInterfaces: boolean, labels: { extends: string; implements: string }): SchemaProjection {
  // Instances are projected separately by RelationshipGraph so contracts and reality never blur together.
  const concrete = classes.filter((item) => !item.abstract)
  const familyRoots = classes.filter((item) => item.abstract && item.parent === null)
  const classIds = new Set(classes.map((item) => String(item.id)))
  const classById = new Map(classes.map((item) => [String(item.id), item]))
  const interfaceByKey = new Map(interfaces.map((item) => [item.key, item]))
  const actionsByTarget = new Map<string, number>()
  actionTypes.forEach((action) => actionsByTarget.set(String(action.target.id), (actionsByTarget.get(String(action.target.id)) || 0) + 1))
  const backingByClass = new Map<string, number>()
  bindings.filter((binding) => binding.ontology_class).forEach((binding) => {
    const id = String(binding.ontology_class!.id)
    backingByClass.set(id, (backingByClass.get(id) || 0) + 1)
  })
  const nodes: SchemaNodeRecord[] = [...familyRoots, ...concrete].map((item) => ({
    id: `class:${item.id}`, recordId: String(item.id), kind: item.abstract ? 'family' : 'class', name: item.name, description: item.description,
    familyId: `class:${item.family.id}`,
    group: groupForFamily(item.family.key), source: item.source, status: item.status,
    propertyCount: item.property_schema.length, elementCount: item.abstract ? concrete.filter((candidate) => candidate.family.id === item.id).length : item.element_count,
    linkCount: linkTypes.filter((link) => item.abstract
      ? [link.from_class, link.to_class].some((endpoint) => String(endpoint.id) === String(item.id) || String(classById.get(String(endpoint.id))?.family.id) === String(item.id))
      : item.lineage.includes(link.from_class.key) || item.lineage.includes(link.to_class.key)).length,
    actionCount: actionTypes.filter((action) => action.target.kind === 'interface'
      ? item.interface_keys.includes(interfaces.find((contract) => String(contract.id) === String(action.target.id))?.key || '')
      : item.lineage.includes(classById.get(String(action.target.id))?.key || '')).length,
    backingCount: item.abstract
      ? bindings.filter((binding) => classById.get(String(binding.ontology_class?.id))?.family.id === item.id).length
      : backingByClass.get(String(item.id)) || 0,
  }))
  if (showInterfaces) interfaces.forEach((item) => {
    const implementation = concrete.find((candidate) => candidate.interface_keys.includes(item.key))
    nodes.push({
      id: `interface:${item.id}`, recordId: String(item.id), kind: 'interface', name: item.name,
      familyId: implementation ? `class:${implementation.family.id}` : null,
      description: item.description, group: groupForFamily(implementation?.family.key), source: item.source,
      status: item.status, propertyCount: item.property_schema.length, elementCount: item.class_count,
      linkCount: 0, actionCount: actionsByTarget.get(String(item.id)) || 0, backingCount: 0,
    })
  })
  const edges: SchemaEdgeRecord[] = linkTypes.filter((link) => classIds.has(String(link.from_class.id)) && classIds.has(String(link.to_class.id))).map((link) => ({
    id: `link:${link.id}`, source: `class:${link.from_class.id}`, target: `class:${link.to_class.id}`,
    label: link.forward_label, cardinality: link.cardinality === 'unknown' ? null : link.cardinality_label, kind: 'link',
  }))
  concrete.forEach((item) => {
    if (item.parent && classIds.has(String(item.parent.id))) edges.push({ id: `extends:${item.id}`, source: `class:${item.id}`, target: `class:${item.parent.id}`, label: labels.extends, cardinality: null, kind: 'extends' })
    if (showInterfaces) item.interface_keys.forEach((key) => {
      const contract = interfaceByKey.get(key)
      if (contract) edges.push({ id: `implements:${item.id}:${contract.id}`, source: `class:${item.id}`, target: `interface:${contract.id}`, label: labels.implements, cardinality: null, kind: 'implements' })
    })
  })
  return { nodes, edges }
}

export function overviewSchemaProjection(projection: SchemaProjection, linksLabel: string) {
  const families = projection.nodes.filter((node) => node.kind === 'family')
  const familyIds = new Set(families.map((node) => node.id))
  const familyByNode = new Map(projection.nodes.flatMap((node) => node.familyId ? [[node.id, node.familyId] as const] : []))
  const groupOrder = new Map<OntologyGroup, number>(['actors', 'governance', 'work', 'technology', 'information', 'outcomes', 'other'].map((group, index) => [group as OntologyGroup, index]))
  const familyById = new Map(families.map((node) => [node.id, node]))
  const connections = new Map<string, { source: string; target: string; count: number }>()

  projection.edges.filter((edge) => edge.kind === 'link').forEach((edge) => {
    const sourceFamily = familyByNode.get(edge.source)
    const targetFamily = familyByNode.get(edge.target)
    if (!sourceFamily || !targetFamily || sourceFamily === targetFamily || !familyIds.has(sourceFamily) || !familyIds.has(targetFamily)) return
    const sourceRank = groupOrder.get(familyById.get(sourceFamily)?.group || 'other') || 0
    const targetRank = groupOrder.get(familyById.get(targetFamily)?.group || 'other') || 0
    const [source, target] = sourceRank <= targetRank ? [sourceFamily, targetFamily] : [targetFamily, sourceFamily]
    const key = `${source}|${target}`
    const current = connections.get(key)
    connections.set(key, { source, target, count: (current?.count || 0) + 1 })
  })

  return {
    nodes: families,
    edges: [...connections.entries()].map(([key, connection]) => ({
      id: `summary:${key}`, source: connection.source, target: connection.target,
      label: `${connection.count} ${linksLabel}`, cardinality: null, kind: 'summary' as const,
    })),
  }
}

export function interfaceSchemaProjection(projection: SchemaProjection) {
  return { nodes: projection.nodes.filter((node) => node.kind === 'interface'), edges: [] }
}

export function searchSchemaProjection(projection: SchemaProjection, query: string, linksLabel = 'links') {
  const normalized = query.trim().toLocaleLowerCase()
  if (!normalized) return overviewSchemaProjection(projection, linksLabel)
  const matches = new Set(projection.nodes.filter((node) => `${node.name} ${node.description || ''}`.toLocaleLowerCase().includes(normalized)).map((node) => node.id))
  const visible = new Set(matches)
  projection.edges.forEach((edge) => {
    if (matches.has(edge.source)) visible.add(edge.target)
    if (matches.has(edge.target)) visible.add(edge.source)
  })
  return {
    nodes: projection.nodes.filter((node) => visible.has(node.id)),
    edges: projection.edges.filter((edge) => visible.has(edge.source) && visible.has(edge.target)),
  }
}

export function focusSchemaProjection(projection: SchemaProjection, focusId: string | null) {
  if (!focusId) return projection
  const visible = new Set([focusId])
  projection.edges.forEach((edge) => {
    if (edge.source === focusId) visible.add(edge.target)
    if (edge.target === focusId) visible.add(edge.source)
  })
  const focused = projection.nodes.find((node) => node.id === focusId)
  if (focused?.kind === 'class') {
    const ancestry = new Set([focusId])
    let currentId = focusId
    while (currentId) {
      const parent = projection.edges.find((edge) => edge.kind === 'extends' && edge.source === currentId)?.target
      if (!parent || ancestry.has(parent)) break
      ancestry.add(parent); visible.add(parent); currentId = parent
    }
    projection.edges.filter((edge) => edge.kind === 'link').forEach((edge) => {
      if (ancestry.has(edge.source)) visible.add(edge.target)
      if (ancestry.has(edge.target)) visible.add(edge.source)
    })
  }
  return {
    nodes: projection.nodes.filter((node) => visible.has(node.id)),
    edges: projection.edges.filter((edge) => visible.has(edge.source) && visible.has(edge.target)),
  }
}
